/**
 * String registry templates list (mirrors Config Registry Templates tab)
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tooltip, Chip, CircularProgress, Alert, useTheme } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridActionsCellItem
} from "@mui/x-data-grid-pro";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useGlobalConfig } from "context/GlobalContext";
import { BglSecondarySmallButton } from "components/button/BglButtons";
import { StringRegistryApis } from "../api/stringRegistryAPI";
import { StringTemplate } from "../types/stringRegistryTypes";
import { StringCloneTemplateDialog } from "./StringCloneTemplateDialog";
import { createColumnHeader } from "features/config-registry/components/ColumnHeader";

export const StringTemplatesTab = () => {
  const { rc } = useGlobalConfig();
  const navigate = useNavigate();
  const theme = useTheme();

  const [templates, setTemplates] = useState<StringTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [selected, setSelected] = useState<StringTemplate | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const canEdit = rc.permissionsManager.canTemplateEdit();

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await StringRegistryApis.getTemplates(rc);
      if (response.error) {
        setError(response.error.message || "Failed to fetch templates");
      } else {
        const raw = response.data as any;
        const data = raw?.data ?? raw;
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [rc]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const columns: GridColDef[] = [
    {
      field: "serialNo",
      headerName: "#",
      width: 70,
      sortable: false,
      hideable: false,
      resizable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
            {rowIndex + 1}
          </Typography>
        );
      }
    },
    {
      field: "name",
      headerName: "Template Name",
      flex: 1.5,
      minWidth: 200,
      hideable: false,
      resizable: true,
      renderHeader: createColumnHeader("Template Name", "name"),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ""} placement="top" arrow>
          <Box
            sx={{
              fontFamily: "monospace",
              fontSize: "0.85rem",
              color: "primary.main",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      )
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 250,
      resizable: true,
      renderHeader: createColumnHeader("Description", "description"),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || "No description"} placement="top" arrow>
          <Typography variant="body2" color="text.secondary" noWrap>
            {params.value || "-"}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: "module",
      headerName: "Module",
      width: 120,
      resizable: true,
      renderHeader: createColumnHeader("Module", "module"),
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Tooltip title={params.value} placement="top" arrow>
            <Chip label={params.value} size="small" />
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled">
            -
          </Typography>
        )
    },
    {
      field: "items",
      headerName: "# Strings",
      width: 130,
      resizable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => row.valueCount ?? row.items?.length ?? 0,
      renderHeader: createColumnHeader("# Strings", "items"),
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value || 0} size="small" color="primary" variant="outlined" />
      )
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      pinnable: true,
      hideable: false,
      renderHeader: createColumnHeader("Actions", "actions"),
      cellClassName: "actions-column",
      headerClassName: "actions-column-header",
      getActions: (params) => {
        const t = params.row as StringTemplate;
        return [
          <GridActionsCellItem
            key="view"
            icon={
              <Tooltip title="View Template">
                <VisibilityIcon />
              </Tooltip>
            }
            label="View"
            onClick={() => navigate(`/cms/string-registry/templates/${t.documentId}`)}
          />,
          ...(canEdit
            ? [
                <GridActionsCellItem
                  key="clone"
                  icon={
                    <Tooltip title="Clone Template">
                      <ContentCopyIcon />
                    </Tooltip>
                  }
                  label="Clone"
                  onClick={() => {
                    setSelected(t);
                    setCloneOpen(true);
                  }}
                />
              ]
            : [])
        ];
      }
    }
  ];

  if (isLoading && templates.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Pilot-facing bundles of strings with optional per-locale overrides. Open a template to add or remove strings
          (PM / full access). New templates can still be created in Strapi Admin if needed.
        </Typography>
        <BglSecondarySmallButton onClick={() => fetchTemplates()}>Refresh</BglSecondarySmallButton>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: { xs: "calc(100vh - 360px)", sm: "calc(100vh - 320px)" },
          minHeight: 400,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
              : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        }}
      >
        <DataGridPro
          rows={templates}
          columns={columns}
          getRowId={(row) => row.documentId}
          disableRowSelectionOnClick
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          onRowClick={(p) => navigate(`/cms/string-registry/templates/${(p.row as StringTemplate).documentId}`)}
          initialState={{
            columns: {
              columnVisibilityModel: {
                module: false
              }
            }
          }}
          sx={{
            border: "none",
            bgcolor: "background.paper",
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: theme.palette.mode === "dark" ? "grey.800" : "#f8f9fa",
              borderBottom: `2px solid ${theme.palette.divider}`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                fontSize: "0.8125rem",
                color: theme.palette.text.secondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              transition: "background-color 0.15s ease",
              "&:hover": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(25, 118, 210, 0.12)"
                    : "rgba(25, 118, 210, 0.06)"
              }
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"
              }`,
              py: 1,
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
              "&:focus": { outline: "none" },
              "&:focus-within": { outline: "none" }
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `2px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === "dark" ? "grey.800" : "#f8f9fa"
            },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .actions-column-header": {
              borderLeft: `2px solid ${theme.palette.divider}`
            },
            "& .actions-column": {
              borderLeft: `2px solid ${
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
              }`
            }
          }}
        />
      </Box>
      {selected && (
        <StringCloneTemplateDialog
          open={cloneOpen}
          template={selected}
          onClose={() => {
            setCloneOpen(false);
            setSelected(null);
          }}
          onCloned={fetchTemplates}
        />
      )}
    </Box>
  );
};
